import React, { useState, useEffect } from 'react';
import {
  Compass, X, ArrowRight, ArrowLeft, Check, ChevronRight,
  Users, User, Baby, Heart, FileText, HelpCircle,
  Home, Car, Brain, Smile, ClipboardList, DollarSign,
  Phone, Mail, MapPin, Sparkles, Shield, Clock
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Data ───
const ENTRY_OPTIONS = [
  { id: 'new', label: "I'm new to NDIS", icon: Sparkles, desc: "I don't know where to start" },
  { id: 'have_plan', label: "I already have a plan", icon: FileText, desc: "I need help using my plan" },
  { id: 'applying', label: "I'm applying", icon: ClipboardList, desc: "I'm in the process of getting NDIS" },
  { id: 'carer', label: "I'm a family member/carer", icon: Users, desc: "I'm helping someone I care about" },
];

const AGE_OPTIONS = [
  { id: 'child', label: 'Child (0–17)', icon: Baby },
  { id: 'adult', label: 'Adult (18–64)', icon: User },
  { id: 'over65', label: 'Over 65', icon: Heart },
];

const SITUATION_OPTIONS = [
  { id: 'waiting', label: 'Waiting for approval' },
  { id: 'just_approved', label: 'Just approved' },
  { id: 'confused', label: 'Have a plan but confused' },
  { id: 'change_provider', label: 'Want to change providers' },
  { id: 'more_support', label: 'Need more support' },
];

const SUPPORT_OPTIONS = [
  { id: 'daily_living', label: 'Daily living support', icon: Home, desc: 'Help with personal care, meals, household tasks' },
  { id: 'transport', label: 'Transport help', icon: Car, desc: 'Getting to appointments, work, and social activities' },
  { id: 'therapy', label: 'Therapy services', icon: Brain, desc: 'Physio, OT, psychology, counselling' },
  { id: 'social', label: 'Social participation', icon: Smile, desc: 'Community activities, recreation, making friends' },
  { id: 'coordination', label: 'Support coordination', icon: ClipboardList, desc: 'Help understanding and managing your plan' },
  { id: 'plan_management', label: 'Plan management', icon: DollarSign, desc: 'Help managing your NDIS budget and invoices' },
  { id: 'employment', label: 'Employment support', icon: Users, desc: 'Help finding and keeping a job' },
  { id: 'mental_health', label: 'Mental health support', icon: Heart, desc: 'Emotional wellbeing and psychological support' },
];

const ELIGIBILITY_QUESTIONS = [
  { id: 'disability', question: 'Do you have a permanent and significant disability?', help: 'This means a condition that is likely to be lifelong and affects your ability to do everyday things.' },
  { id: 'age', question: 'Are you under 65 years of age?', help: 'The NDIS is available to people aged 0 to 65. If you\'re over 65, other support options exist.' },
  { id: 'residency', question: 'Are you an Australian citizen or permanent resident?', help: 'You need to be an Australian citizen, permanent resident, or hold a Protected Special Category Visa.' },
];

const SUPPORT_INFO = {
  daily_living: { title: 'Daily Living Support', category: 'Core Supports', desc: 'Funded under your Core Supports budget, this covers personal care workers who help with showering, dressing, cooking, cleaning, and other daily tasks. In simple terms — it\'s about having someone there to help with the things that are harder to do on your own.' },
  transport: { title: 'Transport Assistance', category: 'Core Supports', desc: 'Your NDIS plan can include funding for transport to get you to medical appointments, work, social activities, and community events. This might mean a support worker driving you, taxi subsidies, or help learning to use public transport.' },
  therapy: { title: 'Therapy Services', category: 'Capacity Building', desc: 'Funded under Capacity Building, this includes physiotherapy, occupational therapy, speech therapy, and psychology. These are specialists who help improve how your body and mind work — so you can do more of what matters to you.' },
  social: { title: 'Community & Social Participation', category: 'Core Supports', desc: 'This funding helps you get out into the community — joining clubs, attending events, trying new activities, and building friendships. It\'s about living a full life, not just an supported one.' },
  coordination: { title: 'Support Coordination', category: 'Capacity Building', desc: 'A Support Coordinator helps you understand your plan, connect with the right providers, and make sure you\'re getting value from your funding. Think of them as your NDIS guide — someone in your corner who knows the system inside out.' },
  plan_management: { title: 'Plan Management', category: 'Capacity Building', desc: 'A Plan Manager handles the financial side of your NDIS plan — processing invoices, tracking budgets, and giving you the freedom to choose any provider. It means less paperwork stress for you.' },
  employment: { title: 'Employment Support', category: 'Capacity Building', desc: 'This includes help finding a job, work experience programs, resume building, interview skills, and on-the-job support. It\'s about finding meaningful work that matches your strengths and interests.' },
  mental_health: { title: 'Mental Health Support', category: 'Core Supports / Capacity Building', desc: 'From counselling and psychology to day-to-day emotional support, this covers everything you need to look after your mental wellbeing. Because your mind matters just as much as everything else.' },
};

const SERVICE_MAPPING = {
  daily_living: { service: 'NDIS Disability Support', link: '/services/disability-support' },
  transport: { service: 'NDIS Disability Support', link: '/services/disability-support' },
  therapy: { service: 'Therapies on NDIS', link: '/services/therapies' },
  social: { service: 'Recreation on NDIS', link: '/services/recreation' },
  coordination: { service: 'NDIS Support Coordination', link: '/services/support-coordination' },
  plan_management: { service: 'NDIS Consulting Services', link: '/services/ndis-consulting' },
  employment: { service: 'Employment on NDIS', link: '/services/employment' },
  mental_health: { service: 'Mental Health Support', link: '/services/mental-health' },
};

// ─── Sub-components ───

const ProgressBar = ({ current, total }) => (
  <div className="flex items-center gap-2 mb-6">
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        className="h-1.5 flex-1 rounded-full transition-all duration-500"
        style={{ backgroundColor: i <= current ? '#81b956' : '#e5e7eb' }}
      />
    ))}
  </div>
);

const OptionCard = ({ icon: Icon, label, desc, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 flex items-center gap-3 ${
      selected ? 'shadow-md' : 'hover:shadow-sm'
    }`}
    style={{
      borderColor: selected ? '#81b956' : '#e5e7eb',
      backgroundColor: selected ? '#81b95610' : 'white',
    }}
    data-testid={`navigator-option-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
  >
    {Icon && (
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: selected ? '#81b956' : '#f3f4f6' }}
      >
        <Icon className="w-5 h-5" style={{ color: selected ? 'white' : '#587740' }} />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-sm" style={{ color: '#587740' }}>{label}</p>
      {desc && <p className="text-xs mt-0.5" style={{ color: '#999' }}>{desc}</p>}
    </div>
    {selected && <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#81b956' }} />}
  </button>
);

// ─── Main Component ───

const NDISPathwayNavigator = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [entry, setEntry] = useState('');
  const [age, setAge] = useState('');
  const [situation, setSituation] = useState('');
  const [supports, setSupports] = useState([]);
  const [eligibility, setEligibility] = useState({});
  const [budget, setBudget] = useState({ core: '', capacity: '' });
  const [lead, setLead] = useState({ name: '', email: '', phone: '', suburb: '' });
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(null);
  const [cookieBannerVisible, setCookieBannerVisible] = useState(false);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setMobileMenuOpen(document.body.classList.contains('mobile-menu-open'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const checkBanner = () => {
      const banner = document.querySelector('[data-testid="cookie-consent-banner"]');
      setCookieBannerVisible(!!banner);
    };
    checkBanner();
    const observer = new MutationObserver(checkBanner);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const reset = () => {
    setStep(0);
    setEntry('');
    setAge('');
    setSituation('');
    setSupports([]);
    setEligibility({});
    setBudget({ core: '', capacity: '' });
    setLead({ name: '', email: '', phone: '', suburb: '' });
    setLeadSubmitted(false);
    setShowInfo(null);
  };

  const toggleSupport = (id) => {
    setSupports((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);
  };

  const submitLead = async () => {
    if (!lead.name || !lead.email) {
      toast.error('Please enter your name and email.');
      return;
    }
    setLeadLoading(true);
    try {
      const message = `[NDIS PATHWAY NAVIGATOR LEAD]
Entry: ${ENTRY_OPTIONS.find(o => o.id === entry)?.label || 'N/A'}
Age: ${AGE_OPTIONS.find(o => o.id === age)?.label || 'N/A'}
Situation: ${SITUATION_OPTIONS.find(o => o.id === situation)?.label || 'N/A'}
Recommended Supports: ${supports.map(s => SUPPORT_INFO[s]?.title).join(', ')}
Suburb: ${lead.suburb || 'Not provided'}`;

      const res = await fetch(`${API_URL}/api/send-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: lead.name,
          email: lead.email,
          message,
          interests: '',
          honeypot: '',
        }),
      });
      if (!res.ok) throw new Error('Failed');
      setLeadSubmitted(true);
      toast.success("We've received your details! We'll be in touch soon.");
    } catch {
      toast.error('Something went wrong. Please try calling us.');
    } finally {
      setLeadLoading(false);
    }
  };

  const coreHours = budget.core ? Math.round(parseFloat(budget.core) / 60) : 0;
  const capacityHours = budget.capacity ? Math.round(parseFloat(budget.capacity) / 200) : 0;
  const eligibleCount = Object.values(eligibility).filter(Boolean).length;

  const STEPS = ['start', 'age', 'situation', 'supports', 'eligibility', 'budget', 'results'];
  const totalSteps = STEPS.length;
  const currentStepName = STEPS[step];

  // ─── Render Steps ───

  const renderStart = () => (
    <div className="space-y-3">
      <div className="text-center mb-4">
        <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: '#81b956' }}>
          <Compass className="w-7 h-7 text-white" />
        </div>
        <h3 className="text-xl font-bold" style={{ color: '#587740' }}>Let's find the right NDIS support for you.</h3>
        <p className="text-sm mt-1" style={{ color: '#999' }}>Answer a few simple questions and we'll create a personalised plan.</p>
      </div>
      {ENTRY_OPTIONS.map((opt) => (
        <OptionCard
          key={opt.id}
          icon={opt.icon}
          label={opt.label}
          desc={opt.desc}
          selected={entry === opt.id}
          onClick={() => { setEntry(opt.id); setStep(1); }}
        />
      ))}
    </div>
  );

  const renderAge = () => (
    <div className="space-y-3">
      <h3 className="text-lg font-bold mb-1" style={{ color: '#587740' }}>What age group are we looking at?</h3>
      <p className="text-sm mb-3" style={{ color: '#999' }}>This helps us tailor the right supports for you or your loved one.</p>
      {AGE_OPTIONS.map((opt) => (
        <OptionCard
          key={opt.id}
          icon={opt.icon}
          label={opt.label}
          selected={age === opt.id}
          onClick={() => { setAge(opt.id); setStep(2); }}
        />
      ))}
    </div>
  );

  const renderSituation = () => (
    <div className="space-y-3">
      <h3 className="text-lg font-bold mb-1" style={{ color: '#587740' }}>What best describes your current situation?</h3>
      <p className="text-sm mb-3" style={{ color: '#999' }}>No wrong answers — just helps us understand where you're at.</p>
      {SITUATION_OPTIONS.map((opt) => (
        <OptionCard
          key={opt.id}
          label={opt.label}
          selected={situation === opt.id}
          onClick={() => { setSituation(opt.id); setStep(3); }}
        />
      ))}
    </div>
  );

  const renderSupports = () => (
    <div className="space-y-3">
      <h3 className="text-lg font-bold mb-1" style={{ color: '#587740' }}>What kind of support do you need?</h3>
      <p className="text-sm mb-3" style={{ color: '#999' }}>Select as many as you like — you can always change later.</p>
      <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
        {SUPPORT_OPTIONS.map((opt) => (
          <OptionCard
            key={opt.id}
            icon={opt.icon}
            label={opt.label}
            desc={opt.desc}
            selected={supports.includes(opt.id)}
            onClick={() => toggleSupport(opt.id)}
          />
        ))}
      </div>
    </div>
  );

  const renderEligibility = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold mb-1" style={{ color: '#587740' }}>Quick Eligibility Check</h3>
      <p className="text-sm mb-3" style={{ color: '#999' }}>Answer these 3 questions to see if you may be eligible for the NDIS.</p>
      {ELIGIBILITY_QUESTIONS.map((q) => (
        <div key={q.id} className="p-4 rounded-xl border" style={{ borderColor: eligibility[q.id] === true ? '#81b956' : eligibility[q.id] === false ? '#ef4444' : '#e5e7eb' }}>
          <p className="font-semibold text-sm mb-1" style={{ color: '#587740' }}>{q.question}</p>
          <p className="text-xs mb-3" style={{ color: '#999' }}>{q.help}</p>
          <div className="flex gap-2">
            <button
              onClick={() => setEligibility((p) => ({ ...p, [q.id]: true }))}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                eligibility[q.id] === true ? 'text-white' : 'border'
              }`}
              style={{
                backgroundColor: eligibility[q.id] === true ? '#81b956' : 'white',
                borderColor: '#81b956',
                color: eligibility[q.id] === true ? 'white' : '#587740',
              }}
            >
              Yes
            </button>
            <button
              onClick={() => setEligibility((p) => ({ ...p, [q.id]: false }))}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                eligibility[q.id] === false ? 'text-white bg-red-500' : 'border border-gray-300 text-gray-500'
              }`}
            >
              No
            </button>
          </div>
        </div>
      ))}
      {Object.keys(eligibility).length === 3 && (
        <div
          className="p-4 rounded-xl text-center"
          style={{ backgroundColor: eligibleCount === 3 ? '#81b95620' : '#fef2f2' }}
        >
          {eligibleCount === 3 ? (
            <>
              <Shield className="w-8 h-8 mx-auto mb-2" style={{ color: '#81b956' }} />
              <p className="font-bold" style={{ color: '#587740' }}>You may be eligible for NDIS support!</p>
              <p className="text-sm mt-1" style={{ color: '#5e5e5e' }}>We can help you with your application — completely free.</p>
            </>
          ) : (
            <>
              <HelpCircle className="w-8 h-8 mx-auto mb-2 text-orange-500" />
              <p className="font-bold text-orange-700">You might not meet standard eligibility</p>
              <p className="text-sm mt-1" style={{ color: '#5e5e5e' }}>But don't worry — there may still be options. Contact us and we'll explore every avenue with you.</p>
            </>
          )}
        </div>
      )}
    </div>
  );

  const renderBudget = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold mb-1" style={{ color: '#587740' }}>NDIS Budget Explainer</h3>
      <p className="text-sm mb-3" style={{ color: '#999' }}>If you know your funding amounts, enter them below and we'll show you what they could provide. Don't know? No worries — skip ahead.</p>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold mb-1" style={{ color: '#587740' }}>Core Supports Budget ($)</label>
          <input
            type="number"
            placeholder="e.g. 18000"
            value={budget.core}
            onChange={(e) => setBudget((p) => ({ ...p, core: e.target.value }))}
            className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#81b956] outline-none text-sm"
            data-testid="navigator-budget-core"
          />
          {budget.core && (
            <div className="mt-2 p-3 rounded-lg" style={{ backgroundColor: '#81b95610' }}>
              <p className="text-sm font-semibold" style={{ color: '#587740' }}>
                ~{coreHours} hours of support worker assistance
              </p>
              <p className="text-xs" style={{ color: '#999' }}>Based on ~$60/hr average rate for personal care and community access</p>
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1" style={{ color: '#587740' }}>Capacity Building Budget ($)</label>
          <input
            type="number"
            placeholder="e.g. 9000"
            value={budget.capacity}
            onChange={(e) => setBudget((p) => ({ ...p, capacity: e.target.value }))}
            className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#81b956] outline-none text-sm"
            data-testid="navigator-budget-capacity"
          />
          {budget.capacity && (
            <div className="mt-2 p-3 rounded-lg" style={{ backgroundColor: '#81b95610' }}>
              <p className="text-sm font-semibold" style={{ color: '#587740' }}>
                ~{capacityHours} hours of therapy & coordination
              </p>
              <p className="text-xs" style={{ color: '#999' }}>Based on ~$200/hr average rate for allied health and support coordination</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderResults = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: '#81b956' }}>
          <Sparkles className="w-7 h-7 text-white" />
        </div>
        <h3 className="text-xl font-bold" style={{ color: '#587740' }}>Your Personalised Support Plan</h3>
        <p className="text-sm mt-1" style={{ color: '#999' }}>Based on your answers, here's what we recommend.</p>
      </div>

      {/* Recommended Supports */}
      <div className="space-y-2">
        <p className="text-sm font-bold" style={{ color: '#587740' }}>Recommended Supports</p>
        {(supports.length > 0 ? supports : ['coordination', 'daily_living']).map((id) => {
          const info = SUPPORT_INFO[id];
          const mapping = SERVICE_MAPPING[id];
          if (!info) return null;
          return (
            <div key={id} className="border rounded-xl overflow-hidden">
              <button
                onClick={() => setShowInfo(showInfo === id ? null : id)}
                className="w-full text-left p-3 flex items-center gap-3 hover:bg-gray-50 transition-colors"
              >
                <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#81b956' }} />
                <div className="flex-1">
                  <p className="font-semibold text-sm" style={{ color: '#587740' }}>{info.title}</p>
                  <p className="text-xs" style={{ color: '#999' }}>{info.category}</p>
                </div>
                <ChevronRight className={`w-4 h-4 transition-transform ${showInfo === id ? 'rotate-90' : ''}`} style={{ color: '#999' }} />
              </button>
              {showInfo === id && (
                <div className="px-3 pb-3 border-t">
                  <p className="text-xs leading-relaxed mt-2 mb-2" style={{ color: '#5e5e5e' }}>{info.desc}</p>
                  {mapping && (
                    <a href={mapping.link} className="inline-flex items-center text-xs font-semibold" style={{ color: '#81b956' }}>
                      View our {mapping.service} <ArrowRight className="w-3 h-3 ml-1" />
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Next Steps */}
      <div className="p-4 rounded-xl" style={{ backgroundColor: '#f8f9fa' }}>
        <p className="text-sm font-bold mb-3" style={{ color: '#587740' }}>Your Next Steps</p>
        <div className="space-y-2">
          {[
            'Book a free NDIS consultation with our team',
            situation === 'confused' || situation === 'change_provider' ? 'Review your current plan with a Support Coordinator' : 'Get matched with the right Support Coordinator',
            entry === 'new' || entry === 'applying' ? 'We\'ll help you apply — completely free' : 'Optimise your plan to get the most from your funding',
          ].map((s, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold text-white" style={{ backgroundColor: '#81b956' }}>{i + 1}</div>
              <p className="text-sm" style={{ color: '#5e5e5e' }}>{s}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Lead Capture */}
      {!leadSubmitted ? (
        <div className="p-4 rounded-xl border-2" style={{ borderColor: '#81b956' }}>
          <p className="text-sm font-bold mb-1" style={{ color: '#587740' }}>Get your free personalised plan</p>
          <p className="text-xs mb-3" style={{ color: '#999' }}>Enter your details and we'll be in touch within 24 hours.</p>
          <div className="space-y-2">
            <input
              placeholder="Your name *"
              value={lead.name}
              onChange={(e) => setLead((p) => ({ ...p, name: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-[#81b956] outline-none"
              data-testid="navigator-lead-name"
            />
            <input
              placeholder="Email address *"
              type="email"
              value={lead.email}
              onChange={(e) => setLead((p) => ({ ...p, email: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-[#81b956] outline-none"
              data-testid="navigator-lead-email"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                placeholder="Phone"
                type="tel"
                value={lead.phone}
                onChange={(e) => setLead((p) => ({ ...p, phone: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-[#81b956] outline-none"
                data-testid="navigator-lead-phone"
              />
              <input
                placeholder="Suburb"
                value={lead.suburb}
                onChange={(e) => setLead((p) => ({ ...p, suburb: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-[#81b956] outline-none"
                data-testid="navigator-lead-suburb"
              />
            </div>
            <Button
              onClick={submitLead}
              disabled={leadLoading}
              className="w-full text-white text-sm py-5 disabled:opacity-60"
              style={{ backgroundColor: '#81b956' }}
              data-testid="navigator-submit-lead"
            >
              {leadLoading ? 'Sending...' : 'Book a Free Support Call'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="p-4 rounded-xl text-center" style={{ backgroundColor: '#81b95620' }}>
          <Check className="w-8 h-8 mx-auto mb-2" style={{ color: '#81b956' }} />
          <p className="font-bold text-sm" style={{ color: '#587740' }}>Thank you! We'll be in touch soon.</p>
          <p className="text-xs mt-1" style={{ color: '#5e5e5e' }}>In the meantime, feel free to explore our services or call us directly.</p>
          <div className="flex gap-2 mt-3 justify-center">
            <a href="tel:+611300404229">
              <Button size="sm" className="text-white text-xs" style={{ backgroundColor: '#81b956' }}>
                <Phone className="w-3 h-3 mr-1" /> Call Us
              </Button>
            </a>
            <a href="/get-on-ndis-free">
              <Button size="sm" variant="outline" className="text-xs border" style={{ borderColor: '#81b956', color: '#81b956' }}>
                Get on NDIS Free
              </Button>
            </a>
          </div>
        </div>
      )}
    </div>
  );

  const stepRenderers = [renderStart, renderAge, renderSituation, renderSupports, renderEligibility, renderBudget, renderResults];

  const canGoNext = () => {
    if (step === 0) return !!entry;
    if (step === 1) return !!age;
    if (step === 2) return !!situation;
    if (step === 3) return supports.length > 0;
    return true;
  };

  return (
    <>
      {/* Sticky Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed left-6 bottom-6 lg:bottom-24 z-[101] flex items-center gap-2 px-5 py-3 rounded-full shadow-2xl text-white font-semibold text-sm transition-all duration-300 hover:scale-105 ${isOpen || mobileMenuOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        style={{ backgroundColor: '#587740' }}
        data-testid="ndis-navigator-btn"
      >
        <Compass className="w-5 h-5" />
        NDIS Pathway Navigator
        <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 animate-ping" />
        <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500" />
      </button>

      {/* Modal Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:justify-start sm:pl-6 sm:pb-6">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setIsOpen(false)} />

          {/* Navigator Panel */}
          <div
            className="relative bg-white w-full sm:w-[420px] max-h-[85vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom duration-300"
            data-testid="ndis-navigator-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ backgroundColor: '#587740' }}>
              <div className="flex items-center gap-2 text-white">
                <Compass className="w-5 h-5" />
                <span className="font-bold text-sm">NDIS Pathway Navigator</span>
              </div>
              <div className="flex items-center gap-2">
                {step > 0 && (
                  <button onClick={reset} className="text-white/70 hover:text-white text-xs underline">
                    Start over
                  </button>
                )}
                <button onClick={() => setIsOpen(false)} className="text-white/70 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Progress */}
            <div className="px-5 pt-4">
              <ProgressBar current={step} total={totalSteps} />
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 pb-4">
              {stepRenderers[step]()}
            </div>

            {/* Footer Nav */}
            {step > 0 && (
              <div className="flex items-center justify-between px-5 py-3 border-t bg-gray-50">
                <button
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  className="flex items-center gap-1 text-sm font-medium"
                  style={{ color: '#587740' }}
                  data-testid="navigator-back-btn"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                {step < totalSteps - 1 && (
                  <Button
                    onClick={() => setStep((s) => Math.min(totalSteps - 1, s + 1))}
                    disabled={!canGoNext()}
                    size="sm"
                    className="text-white text-sm disabled:opacity-40"
                    style={{ backgroundColor: '#81b956' }}
                    data-testid="navigator-next-btn"
                  >
                    Next <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default NDISPathwayNavigator;
